import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface RedactionOptions {
  isEnabled: boolean;
  structuredPii: Set<string>;
}

const redactionPluginKey = new PluginKey('redaction');

/**
 * Build decoration spans by scanning document text nodes for structured PII
 * matches (word-boundary aware) and piiRedact marks from the converter.
 */
function buildDecorations(doc: any, isEnabled: boolean, structuredPii: Set<string>): DecorationSet {
  if (!isEnabled) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  const isWordChar = (ch: string | undefined) => !!ch && /\w/.test(ch);

  doc.descendants((node: any, pos: number) => {
    if (!node.isText) return;

    const text = node.text as string;
    const nodeStart = pos;

    // Source 1: piiRedact marks on the node
    if (node.marks) {
      for (const mark of node.marks) {
        if (mark.type.name === 'piiRedact') {
          decorations.push(
            Decoration.inline(nodeStart, nodeStart + text.length, {
              class: 'redaction-blur',
              'aria-label': 'Redacted content — hover to reveal',
            })
          );
          // Entire node is marked — no need to also do structured matching on it
          return;
        }
      }
    }

    // Source 2: structured PII string matching with word boundaries
    if (structuredPii.size === 0) return;

    const lowerText = text.toLowerCase();

    for (const piiValue of structuredPii) {
      const lowerPii = piiValue.toLowerCase();
      let searchFrom = 0;

      while (searchFrom < lowerText.length) {
        const idx = lowerText.indexOf(lowerPii, searchFrom);
        if (idx === -1) break;

        const end = idx + piiValue.length;
        const boundedStart = !isWordChar(lowerText[idx - 1]);
        const boundedEnd = !isWordChar(lowerText[end]);

        if (boundedStart && boundedEnd) {
          decorations.push(
            Decoration.inline(nodeStart + idx, nodeStart + end, {
              class: 'redaction-blur',
              'aria-label': 'Redacted content — hover to reveal',
            })
          );
        }

        searchFrom = idx + 1;
      }
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const Redaction = Extension.create<RedactionOptions>({
  name: 'redaction',

  addOptions() {
    return {
      isEnabled: false,
      structuredPii: new Set<string>(),
    };
  },

  addProseMirrorPlugins() {
    const extensionOptions = this.options;

    return [
      new Plugin({
        key: redactionPluginKey,

        state: {
          init(_, { doc }) {
            return buildDecorations(doc, extensionOptions.isEnabled, extensionOptions.structuredPii);
          },

          apply(tr, oldDecorations, _oldState, newState) {
            // Check for explicit redaction update via transaction metadata
            const meta = tr.getMeta(redactionPluginKey);
            if (meta) {
              return buildDecorations(newState.doc, meta.isEnabled, meta.structuredPii);
            }

            // If the document changed, full rebuild (remapping is fragile near PII edits)
            if (tr.docChanged) {
              return buildDecorations(
                newState.doc,
                extensionOptions.isEnabled,
                extensionOptions.structuredPii
              );
            }

            return oldDecorations;
          },
        },

        props: {
          decorations(state) {
            return redactionPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});

export { redactionPluginKey };
